import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Selector } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';

import styles from './Selectors.css';
import { getMapLayers } from '../../config/mapLayers';
import { getMapLayerForSeason } from '../../utils/selectorOptions';
import { logDmbError } from '../../utils/error';
import { switchView } from '../../utils/url';
import { trackEvent, trackHeapEvent } from '../../actions/analyticsActions';
import { useAppSelector } from '../../utils/useAppSelector';
import { FeatureFlagKey, FHA_KEY } from '../../types';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import type { AppState } from '../../store/types/Store';
import type { FieldState } from '../../actions/field/types/state';
import { LayersBySeason } from '../../types/Layer';

type ActiveSeason = {
  id: string;
  name: string;
};

type ActiveMapLayer = {
  id: string;
  name: string;
};

type LayersStateSlice = {
  activeMapLayers: ActiveMapLayer[];
  disabledMapLayers: string[];
  isSplitViewEnabled: boolean;
  list: LayersBySeason;
};

type SeasonsMap = Record<string, { name: string }>;

interface SelectorsProps {
  index: number;
  activeMapLayer: ActiveMapLayer;
  activeSeason: ActiveSeason;
  activeSeasons: ActiveSeason[];
  disabledMapLayers: string[];
  fieldId: string;
  hasHarvestDataForActiveSeason: boolean;
  harvestArdEnabled: boolean;
  isSplitViewButtonVisible: boolean;
  isSplitViewEnabled: boolean;
  layers: LayersStateSlice;
  seasons: SeasonsMap;
  sortedSeasonKeys: string[];
  trackAnalyticsEvent: (category: string, action: string) => void;
  trackAnalyticsHeapEvent: (eventName: string) => void;
}

const Selectors = (props: SelectorsProps) => {
  const {
    activeSeason,
    activeMapLayer,
    disabledMapLayers,
    layers,
    seasons,
    sortedSeasonKeys,
    isSplitViewEnabled,
    isSplitViewButtonVisible,
    activeSeasons,
    index,
    fieldId,
    harvestArdEnabled,
    hasHarvestDataForActiveSeason,
    trackAnalyticsEvent,
    trackAnalyticsHeapEvent
  } = props;

  const fieldAssets = useAppSelector(state => state.field.features);
  const hideSpatialDisplayFeatureLDFlag = useFeatureFlag(FeatureFlagKey.DMB_HIDE_SPATIAL_DISPLAY_FEATURE);
  const agVizEnabled = useFeatureFlag(FeatureFlagKey.DMB_WEB_AG_VIZ);

  useEffect(() => {
    if (layers.activeMapLayers.some((layer) => layer.id === FHA_KEY)) {
      trackAnalyticsHeapEvent('View FHA Image');
    }
  }, [layers.activeMapLayers, trackAnalyticsHeapEvent]);

  const updateSelectorVals = useCallback((layerId: string, seasonId: string) => {
    const seasonIds = activeSeasons.map((season) => season.id);
    const layerIds = layers.activeMapLayers.map((layer) => layer.id);
    switchView(index, seasonId, seasonIds, layerId, layerIds, fieldId);
  }, [index, fieldId, activeSeasons, layers.activeMapLayers]);

  const setSeason = useCallback((_: unknown, id: string) => {
    try {
      const mapLayer = getMapLayerForSeason(layers.list, activeMapLayer.id, id);
      updateSelectorVals(mapLayer.id, id);

      trackAnalyticsEvent('season', 'selected');
    } catch (error) {
      logDmbError(error, {
        appLocation: 'season selector',
        seasonId: id,
      });
    }
  }, [layers.list, activeMapLayer.id, updateSelectorVals, trackAnalyticsEvent]);

  const setMap = useCallback((_: unknown, id: string) => {
    try {
      updateSelectorVals(id, activeSeason.id);
      trackAnalyticsEvent('map', 'selected');
    } catch (error) {
      logDmbError(error, {
        appLocation: 'map layer selector',
        mapLayerId: id,
      });
    }
  }, [updateSelectorVals, activeSeason.id, trackAnalyticsEvent]);

  const seasonOptions = useMemo(
    () => sortedSeasonKeys.map((key) => ({
      name: seasons[key].name,
      id: key,
    })),
    [sortedSeasonKeys, seasons]
  );

  const shouldApplyHarvestDataGate = agVizEnabled && harvestArdEnabled;
  const effectiveHasHarvestDataForActiveSeason = shouldApplyHarvestDataGate
    ? hasHarvestDataForActiveSeason
    : true;

  const mapOptions = useMemo(() => getMapLayers(
    activeSeason.id,
    layers.list,
    disabledMapLayers,
    fieldAssets,
    hideSpatialDisplayFeatureLDFlag,
    effectiveHasHarvestDataForActiveSeason,
    agVizEnabled
  ), [
    activeSeason.id,
    layers.list,
    disabledMapLayers,
    fieldAssets,
    hideSpatialDisplayFeatureLDFlag,
    effectiveHasHarvestDataForActiveSeason,
    agVizEnabled
  ]);

  const className = `${styles.selectorsContainer} ${isSplitViewButtonVisible ? styles.selectorsContainerSpaced : ''
    }
     ${isSplitViewEnabled ? styles.selectorsContainerSplit : ''}`;

  return (
    <div className={className} data-test-id="dmb-selectors-container">
      <Selector
        className={styles.selectorSeason}
        name={getMessage('season')}
        options={seasonOptions}
        setValue={setSeason}
        testId="dmb-season-selector"
        value={activeSeason.id}
        valueName={activeSeason.name}
      />
      <Selector
        className={styles.selector}
        name={getMessage('maps')}
        options={mapOptions}
        setValue={setMap}
        testId="dmb-map-layer-selector"
        value={activeMapLayer.id}
        valueName={activeMapLayer.name}
      />
    </div>
  );
};

function mapStateToProps(
  state: AppState,
  prop: Pick<SelectorsProps, 'index'>
): Omit<SelectorsProps, 'trackAnalyticsEvent' | 'trackAnalyticsHeapEvent'> {
  const { layers } = state;
  const field = state.field as FieldState;
  const { activeSeasons, list, sortedSeasonKeys } = state.seasons;
  const activeSeason = (activeSeasons[prop.index] || activeSeasons[0]) as ActiveSeason;
  const activeMapLayer = (layers.activeMapLayers[prop.index] || layers.activeMapLayers[0]) as ActiveMapLayer;

  return {
    index: prop.index,
    activeSeasons: activeSeasons as ActiveSeason[],
    activeSeason,
    activeMapLayer,
    disabledMapLayers: layers.disabledMapLayers as string[],
    fieldId: field.id || '',
    hasHarvestDataForActiveSeason: Boolean(activeSeason?.id && state.harvest?.[activeSeason.id]),
    harvestArdEnabled: Boolean(state.feature?.userConfigFeatures?.[FeatureFlagKey.WEB_HARVEST_ARD_DATA]?.value),
    isSplitViewEnabled: layers.isSplitViewEnabled,
    isSplitViewButtonVisible: !layers.isSplitViewEnabled,
    layers: layers as unknown as LayersStateSlice,
    seasons: list as SeasonsMap,
    sortedSeasonKeys: sortedSeasonKeys as string[],
  };
}

const mapDispatchToProps = {
  trackAnalyticsEvent: trackEvent,
  trackAnalyticsHeapEvent: trackHeapEvent
};

export default connect(mapStateToProps, mapDispatchToProps)(Selectors);
